import Menu from "@mui/material/Menu";
import React, { useState } from "react";
import { TiFilter } from "react-icons/ti";
import { isFilterActive } from "../../utils/filters";
import {
  AutocompleteOption,
  FilterDefinition,
  TableColumn,
} from "../../utils/interfaces";
import { BooleanFilter } from "./BooleanFilter";
import { DateFilter } from "./DateFilter";
import { SelectFilter } from "./SelectFilter";

interface FilterHeaderProps<T> {
  column: TableColumn<T>;
  value: any;
  onChange: (value: any) => void;
  dynamicOptions?: Record<string, any[]>; // for dynamic filters
  children: React.ReactNode;
  dynamicFilterOptions?: AutocompleteOption[];
}

const FILTER_COMPONENTS: Record<
  FilterDefinition["type"],
  React.FC<{
    value: any;
    onChange: (val: any) => void;
    options: any;
  }>
> = {
  dateRange: ({ value, onChange, options }) => (
    <DateFilter
      value={value}
      onChange={onChange}
      title={options.title}
      disableFuture={options.disableFuture}
    />
  ),
  select: ({ value, onChange, options }) => (
    <SelectFilter
      value={value}
      onChange={onChange}
      options={options.options}
      multiple={options.multiple}
      label={options.label}
    />
  ),
  boolean: ({ value, onChange, options }) => (
    <BooleanFilter value={value} onChange={onChange} label={options.label} />
  ),
};

export function FilterHeader<T>({
  column,
  value,
  onChange,
  children,
  dynamicFilterOptions,
}: FilterHeaderProps<T>) {
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  if (!column.filter) return <>{children}</>;
  const { type, options } = column.filter;
  const Component = FILTER_COMPONENTS[type];
  const isActive = isFilterActive(type, value);

  const resolvedOptions =
    type === "select"
      ? {
          ...options,
          options: dynamicFilterOptions ?? options.options,
        }
      : options;

  return (
    <div
      className={`flex items-center ${
        column.align === "center" ? "justify-center" : ""
      } ${column.order ? "" : "gap-2"} [&:hover_.opacity-0]:opacity-50`}
    >
      {children}
      <div
        className={`h-full cursor-pointer ${
          anchorEl || isActive ? "" : "opacity-0"
        } transition-[opacity,transform] duration-200 ease-in-out`}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        data-testid={`filterToggle-${column.id}`}
      >
        <TiFilter size={16} className='min-w-4 min-h-4 text-slate-500' />
      </div>
      <Menu
        data-testid={`filterMenu-${column.id}`}
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        classes={{
          paper:
            "text-sm px-3 py-1 !text-slate-400 font-medium !rounded-lg mt-2",
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Component
          value={value}
          onChange={onChange}
          options={resolvedOptions}
        />
      </Menu>
    </div>
  );
}
